import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronRight, FlaskConical, LayoutGrid, List, Package, SlidersHorizontal } from 'lucide-react';

import { loadLaboratorySamples } from './api';
import { useXomicsAuth } from './AuthContext';
import LaboratoryCollectionModal, { CollectionState, TubeProgress } from './LaboratoryCollectionModal';
import { LabPagination, useLabSearch } from './LaboratoryParts';
import { ownSampleTypeName } from './LaboratoryText';
import { FilterMenu, ago, useNow, when } from './MobileParts';
import { tx } from './i18n';
import { Empty, Failed, Head, Loading, SearchField } from './parts';
import { useLiveResource } from './useLive';
import './laboratory-inventory.css';

const PAGE_SIZE = 100;
// What the laboratory does next with a collection, in the order it does it.
const STATES = ['all', 'awaiting_acceptance', 'on_hold', 'ready', 'assigned', 'quarantined'];
const label = (key, values) => tx(`ui.lab.inventory.${key}`, values);

// Receipt notifications must not depend on the visible search, filter or page.
// Keep reading through the previous timestamp so simultaneous receipts are
// compared by UUID, including a batch that crosses a page boundary.
async function readReadyReceipts(checkpoint) {
  const rows = [];
  let offset = 0, cutoff = checkpoint === null ? null : checkpoint.at;
  while (true) {
    const page = await loadLaboratorySamples({ status: 'ready', limit: PAGE_SIZE, offset });
    if (cutoff === null) cutoff = page.samples.length === 0 ? 0 : page.samples[0].lastReceivedAt;
    const pageCutoff = cutoff;
    rows.push(...page.samples.filter(row => row.lastReceivedAt >= pageCutoff));
    if (page.samples.length < PAGE_SIZE || page.samples[page.samples.length - 1].lastReceivedAt < cutoff) {
      return [...new Map(rows.map(row => [row.collectionUuid, row])).values()];
    }
    offset += PAGE_SIZE;
  }
}

// Who took the collection in at the laboratory, and when: its latest receipt.
export function latestReceipt(row) {
  return row.physicalSamples.filter(sample => sample.receivedAt !== null)
    .reduce((latest, sample) => (latest === null || sample.receivedAt > latest.receivedAt ? sample : latest), null);
}

function InventoryRow({ row, now, onOpen }) {
  const receipt = latestReceipt(row);
  return <button type="button" className="xo-lab-inventory-row" onClick={() => onOpen(row)} aria-label={label('open_details', { code: row.printedCode ?? row.code })}>
    <TubeProgress samples={row.physicalSamples} />
    <span className="xo-lab-inventory-identity"><code>{row.printedCode ?? row.code}</code><strong>{ownSampleTypeName(row.sampleType)}</strong>
      {(row.product !== null || row.organization !== null) && <span>
        {row.product !== null && <span className="xo-lab-inventory-product"><Package aria-hidden="true" />{row.product.name}</span>}
        {row.organization !== null && <span className="xo-lab-inventory-product">{row.organization.name}</span>}</span>}
    </span>
    {receipt !== null && <span className="xo-lab-inventory-receipt">
      <strong>{receipt.receivedBy === null ? label('received') : receipt.receivedBy.name}</strong>
      <small title={when(receipt.receivedAt)}>{label('received_ago', { when: ago(receipt.receivedAt, now) })}</small>
    </span>}
    <span className="xo-lab-inventory-state"><CollectionState row={row} />
      {row.totalSamples > 1 && <small>{label('progress', { received: row.receivedSamples, total: row.totalSamples })}</small>}</span>
    <ChevronRight className="xo-lab-inventory-open" aria-hidden="true" />
  </button>;
}

export default function XomicsLaboratory() {
  const { permissions } = useXomicsAuth();
  const mayTrack = permissions.includes('SAMPLE_TRACK_READ') || permissions.includes('SAMPLE_TRACK_READ_ALL');
  const { query, setQuery, search } = useLabSearch();
  const [status, setStatus] = useState('all'), [sampleType, setSampleType] = useState('');
  const [offset, setOffset] = useState(0), [view, setView] = useState('list');
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [readyQueue, setReadyQueue] = useState([]);
  const receiptCheckpoint = useRef(null), observedReceipts = useRef(null);
  const now = useNow(30000);
  const load = useCallback(async () => {
    const [inventory, readyReceipts] = await Promise.all([
      loadLaboratorySamples({ limit: PAGE_SIZE, offset, query: search, status, sampleType }),
      readReadyReceipts(receiptCheckpoint.current),
    ]);
    return { ...inventory, readyReceipts };
  }, [offset, search, status, sampleType]);
  const resource = useLiveResource(['samples'], load);
  const data = resource.data;
  useEffect(() => {
    if (data === null || observedReceipts.current === data) return;
    observedReceipts.current = data;
    const previous = receiptCheckpoint.current, rows = data.readyReceipts;
    const at = Math.max(previous === null ? 0 : previous.at, ...rows.map(row => row.lastReceivedAt));
    const seen = new Set(previous !== null && previous.at === at ? previous.seen : []);
    for (const row of rows) if (row.lastReceivedAt === at) seen.add(row.collectionUuid);
    receiptCheckpoint.current = { at, seen };
    if (previous === null) return;
    const completed = rows.filter(row => (row.lastReceivedAt > previous.at || (row.lastReceivedAt === previous.at && !previous.seen.has(row.collectionUuid)))
      && (selectedCollection === null || selectedCollection.uuid !== row.collectionUuid));
    if (completed.length) setReadyQueue(queued => [...queued, ...completed
      .filter(row => !queued.some(item => item.uuid === row.collectionUuid))
      .map(row => ({ uuid: row.collectionUuid, code: row.printedCode ?? row.code }))]);
  }, [data, selectedCollection]);
  useEffect(() => {
    if (selectedCollection !== null || readyQueue.length === 0) return;
    setSelectedCollection(readyQueue[0]);
    setReadyQueue(queued => queued.slice(1));
  }, [readyQueue, selectedCollection]);
  const current = data !== null && data.filters.query === query.trim() && data.filters.status === status
    && data.filters.sampleType === sampleType && data.offset === offset;
  const countsCurrent = data !== null && data.filters.query === query.trim() && data.filters.sampleType === sampleType;
  const filtered = query.trim() !== '' || status !== 'all' || sampleType !== '';
  const change = setter => value => { setter(value); setOffset(0); setSelectedCollection(null); };

  return <div className="xo-products-page xo-lab-inventory-page">
    <Head title={label('title')} subtitle={label('subtitle')} />
    <div className="xo-product-navigation">
      <div className="xo-product-toolbar">
        <SearchField className="xo-product-search" value={query} onChange={change(setQuery)} maxLength={120} placeholder={label('search_tubes')} />
        <div className="xo-product-toolbar-actions">
          <FilterMenu icon={SlidersHorizontal} label={tx('ui.lab.status')} value={status} onChange={change(setStatus)}
            options={STATES.map(value => ({ value, label: label(`tab.${value}`), count: countsCurrent ? data.statusCounts[value] : undefined }))} />
          <FilterMenu icon={FlaskConical} label={label('sample_type')} value={sampleType} onChange={change(setSampleType)}
            options={[{ value: '', label: label('all_types') },
              ...(data === null ? [] : data.sampleTypes.map(type => ({ value: type.code, label: ownSampleTypeName(type) })))]} />
          <div className="xo-product-view-switch" role="group" aria-label={label('layout')}>
            {[['grid', LayoutGrid, 'ui.products.grid_701c483f'], ['list', List, 'ui.products.list_a1fffaaa']].map(([value, Icon, key]) =>
              <button type="button" key={value} className={view === value ? 'is-active' : ''} aria-pressed={view === value} onClick={() => setView(value)}>
                <Icon aria-hidden="true" /><span>{tx(key)}</span></button>)}
          </div>
        </div>
      </div>
    </div>
    <section aria-busy={!resource.error && (resource.loading || !current)}>
      {resource.error ? <Failed message={resource.error} onRetry={resource.refresh} />
        : !current ? <Loading label={tx('ui.lab.loading')} />
          : <>
            <div className="xo-product-results-heading" role="status"><span>{label(data.total === 1 ? 'collection' : 'collection_count', { count: data.total })}</span></div>
            {data.samples.length === 0 ? <Empty title={label(filtered ? 'no_matches' : 'empty')} detail={label(filtered ? 'no_matches_detail' : 'empty_detail')} />
              : <div className={`xo-lab-inventory is-${view}`}>{data.samples.map(row => <InventoryRow key={row.collectionUuid} row={row} now={now}
                onOpen={opened => setSelectedCollection({ uuid: opened.collectionUuid, code: opened.printedCode ?? opened.code })} />)}</div>}
            <LabPagination data={data} loading={resource.loading} onOffset={value => { setOffset(value); setSelectedCollection(null); }} />
          </>}
    </section>
    {selectedCollection !== null && <LaboratoryCollectionModal key={selectedCollection.uuid} collectionUuid={selectedCollection.uuid} code={selectedCollection.code}
      mayTrack={mayTrack} onClose={() => setSelectedCollection(null)} onAssigned={resource.refresh} />}
  </div>;
}
